import React from "react";
import { WifiOff, Wifi, AlertCircle, RefreshCw, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  setOnline,
  setOffline,
  updateConnectionInfo,
  selectIsOnline,
  selectConnectionQuality,
  selectShouldShowConnectionWarning,
  selectConnectionTypeLabel,
} from "../../store/slices/networkSlice";

/**
 * OfflineBanner Component
 *
 * Network status banner with:
 * - Red banner: Offline
 * - Yellow banner: Poor connection (iOS-safe, won't trigger falsely)
 * - Green banner: Back online (temporary)
 * - Blue banner: 5G detected (temporary, celebratory)
 * - Hidden: Online with good/excellent connection
 */

export const OfflineBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("pwa");
  const isOnline = useAppSelector(selectIsOnline);
  const connectionQuality = useAppSelector(selectConnectionQuality);
  const shouldShowWarning = useAppSelector(selectShouldShowConnectionWarning); // iOS-safe
  const connectionType = useAppSelector(selectConnectionTypeLabel);

  const [showOnlineBanner, setShowOnlineBanner] = React.useState(false);
  const [show5GBanner, setShow5GBanner] = React.useState(false);

  React.useEffect(() => {
    const handleOnline = () => {
      dispatch(setOnline());

      // Show "Back online" banner temporarily
      setShowOnlineBanner(true);
      setTimeout(() => setShowOnlineBanner(false), 5000);

      // Check if we're on 5G and show celebratory banner
      dispatch(updateConnectionInfo());
      setTimeout(() => {
        // Access Redux state to check connection type
        const state = (window as any).__REDUX_STORE__?.getState?.();
        if (state?.network?.effectiveType === "5g") {
          setShow5GBanner(true);
          setTimeout(() => setShow5GBanner(false), 4000);
        }
      }, 100);
    };

    const handleOffline = () => {
      dispatch(setOffline());
      setShowOnlineBanner(false);
      setShow5GBanner(false);
    };

    // Browser online/offline events
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Connection change event (if supported)
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;

    const handleConnectionChange = () => {
      if (navigator.onLine) {
        dispatch(setOnline());
      }
      dispatch(updateConnectionInfo()); // Update connection metrics
    };

    if (connection) {
      connection.addEventListener("change", handleConnectionChange);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (connection) {
        connection.removeEventListener("change", handleConnectionChange);
      }
    };
  }, [dispatch]);

  // Don't show if online with good/excellent connection and no banners
  if (
    isOnline &&
    (connectionQuality === "good" || connectionQuality === "excellent") &&
    !showOnlineBanner &&
    !show5GBanner
  ) {
    return null;
  }

  // 1. OFFLINE BANNER (highest priority)
  if (!isOnline) {
    return (
      <div className="bg-red-600 text-white px-4 py-3 shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <WifiOff className="h-5 w-5 shrink-0" />
            <div>
              <p className="text-sm font-semibold">{t("offline.title")}</p>
              <p className="text-xs opacity-90">{t("offline.subtitle")}</p>
            </div>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="text-white hover:text-red-100 p-2 rounded-lg hover:bg-red-700 transition-colors"
            title={t("offline.retryTitle")}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // 2. 5G CELEBRATION BANNER (shows briefly)
  if (show5GBanner && connectionQuality === "excellent") {
    return (
      <div className="bg-blue-600 text-white px-4 py-3 shadow-md sticky top-0 z-50 animate-slide-down">
        <div className="max-w-7xl mx-auto flex items-center space-x-3">
          <Zap className="h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">
              {t("fiveG.title", { connectionType })}
            </p>
            <p className="text-xs opacity-90">{t("fiveG.subtitle")}</p>
          </div>
        </div>
      </div>
    );
  }

  // 3. BACK ONLINE BANNER (temporary)
  if (showOnlineBanner) {
    return (
      <div className="bg-green-600 text-white px-4 py-3 shadow-md sticky top-0 z-50 animate-slide-down">
        <div className="max-w-7xl mx-auto flex items-center space-x-3">
          <Wifi className="h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">{t("backOnline.title")}</p>
            <p className="text-xs opacity-90">{t("backOnline.subtitle")}</p>
          </div>
        </div>
      </div>
    );
  }

  // 4. POOR CONNECTION WARNING (iOS-safe - won't trigger on unsupported platforms)
  if (shouldShowWarning) {
    return (
      <div className="bg-yellow-600 text-white px-4 py-3 shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">
              {t("slowConnection.title", { connectionType })}
            </p>
            <p className="text-xs opacity-90">{t("slowConnection.subtitle")}</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// NOTE: Add this CSS to your global styles or Tailwind config:
// .animate-slide-down {
//   animation: slideDown 0.3s ease-out;
// }
// @keyframes slideDown {
//   from { transform: translateY(-100%); }
//   to { transform: translateY(0); }
// }
